import {createStyles} from '@mantine/core';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import navigateToURL from 'lib/navigateToURL';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {isAgentsFeatureEnabled} from 'llm/isAgentsFeatureEnabled';
import {AGENT_FOUNDRIES_LIST_URL, chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import SidebarRow from './SidebarRow';

const useStyles = createStyles(() => ({
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
}));

export interface ChatSidebarNavProps {
  /** Start a new chat (mirrors the header "New chat" action). */
  onNewChat: () => void;
}

function NavIcon({icon}: {icon: BenchlingIconName}): JSX.Element {
  return <BenchlingIcon icon={icon} />;
}

/**
 * Primary navigation for the redesigned chat sidebar. "New Chat" resets to the welcome panel;
 * "Foundries" opens its own top-level view in the sidepanel's main content slot; the remaining
 * destinations open the chat settings modal on their respective tab. "Artifacts" is planned but not
 * yet implemented, so it is intentionally omitted for now.
 */
export default function ChatSidebarNav({onNewChat}: ChatSidebarNavProps): JSX.Element {
  const {classes} = useStyles();

  const openSettings = useCallback((tab: ChatSettingsTab) => {
    navigateToURL(chatSettingsModalUrl(tab));
  }, []);

  const openFoundries = useCallback(() => {
    navigateToURL(AGENT_FOUNDRIES_LIST_URL);
  }, []);

  return (
    <nav className={classes.nav} aria-label="Chat">
      <SidebarRow
        icon={<BenchlingIcon icon="comment-outline" />}
        label="New Chat"
        onClick={onNewChat}
        hoverHighlight={true}
        dataTestAttrs={{component: 'ChatSidebarNavItem', element: 'root', key: 'new-chat'}}
      />
      {isAgentsFeatureEnabled() && (
        <SidebarRow
          icon={<NavIcon icon="robot-outline" />}
          label="Agents"
          onClick={() => {
            openSettings(ChatSettingsTab.AGENTS);
          }}
          hoverHighlight={true}
          dataTestAttrs={{component: 'ChatSidebarNavItem', element: 'root', key: 'agents'}}
        />
      )}
      {BenchlingConfig.LLM_ENABLE_AGENT_FACTORIES && (
        <SidebarRow
          icon={<NavIcon icon="gears-outline" />}
          label="Foundries"
          onClick={openFoundries}
          hoverHighlight={true}
          dataTestAttrs={{component: 'ChatSidebarNavItem', element: 'root', key: 'foundries'}}
        />
      )}
      <SidebarRow
        icon={<NavIcon icon="bolt-outline" />}
        label="Skills"
        onClick={() => {
          openSettings(ChatSettingsTab.SKILLS);
        }}
        hoverHighlight={true}
        dataTestAttrs={{component: 'ChatSidebarNavItem', element: 'root', key: 'skills'}}
      />
      <SidebarRow
        icon={<NavIcon icon="plug-outline" />}
        label="AI Connectors"
        onClick={() => {
          openSettings(ChatSettingsTab.CONNECTORS);
        }}
        hoverHighlight={true}
        dataTestAttrs={{component: 'ChatSidebarNavItem', element: 'root', key: 'ai-connectors'}}
      />
    </nav>
  );
}
